import React, {useContext, useEffect, useRef, useReducer, useCallback} from "react";
import GenericDialog from "@common/GenericDialog";
import {Form, Input, Radio, Select, Checkbox, Button} from "antd";
import axios from "@common/axios";
import GenericSyllabusTopicConceptSelect from "@common/GenericSyllabusTopicConceptSelect";
import {BrandContext} from "@context/BrandContext";
import CircularLoader from "@common/CircularLoader";
import {UserContext} from "@context/UserContext";
import Alert from "@common/Alert";
import TableAntD from "@common/TableAntD";

const RESOURCE_TYPES = {
    MEDIA: "MEDIA",
    QUESTION: "QUESTION",
    QUESTION_CLUSTER: "QUESTION_CLUSTER",
    TUTORIAL: "TUTORIAL",
    TEST: "TEST",
    QUIZ: "QUIZ",
    CUSTOM_ACTIVITY: "CUSTOM_ACTIVITY",
    SURVEY: "SURVEY"
}
const MEDIA_TYPES = {
    DESMOS: "DESMOS",
    SCORCHER: "SCORCHER",
    WORKSHEET: "WORKSHEET",
    WIDGET: "WIDGET",
    VIDEO: "VIDEO"
}
const QUESTION_TYPES = {
    PRACTICE: "PRACTICE",
    WALKTHROUGH: "WALKTHROUGH"
}
const DIALOG_FIELDS = {
    STEP_SECTION: "stepSection",
    STEP_LEVEL: "stepLevel",
    STEP_QUIZ_LEVEL: "stepQuizLevel",
    STEP_TOTAL: "stepTotal",
    STEP_NAME: "stepName",
    STEP_SURVEY: "stepSurvey",
    STEP_DESCRIPTION: "stepDescription",
    STEP_MSG: "stepMessage",
    QUESTION_OPTIONS: "questionOptions",
    SELECT_EXERCISES: "selectExercises",
    TEST_INFO: "testInfo",
    TEST_SEARCH: "testSearch",
    CUSTOM_ACTIVITY_OPTIONS: "customActivityOptions",
    SELECT_LESSON_OPTIONS: "selectLessonOptions"
}
const AJAX_DATA_TYPES = {
    TRAIL_TUTORIAL_TYPES: "trailTutorialTypes",
    TRAIL_SECTIONS: "trailSections"
}
const INPUTS = {
    ACTIVITIES: "activities",
    ACTIVITY_LEVEL: "activityLevel",
    EXERCISES: "exercises"
}
const ACTIONS = {
    RESET: "reset",
    GENERIC_VALUE_UPDATE: "genericValueUpdate",
    UPDATE_FORM_ERROR: "updateFormError",
    OPEN_PREVIEW: "openPreview",
    CLOSE_PREVIEW: "closePreview",
    UPDATE_FORM_EXTRA_VALIDATION: "updateFormExtraValidation"
}

const defaultPracticeQuestionLevels = [
    {questionLevel: 1, activityCount: 15},
    {questionLevel: 2, activityCount: 15},
    {questionLevel: 3, activityCount: 15},
    {questionLevel: 4, activityCount: 15}
];

const defaultDialogState = {
    init: false,
    loading: true,
    submitting: false,
    activeFields: [],
    currentPage: 1,
    inputState: {},
    ajaxData: {},
    testId: null,
    topicId: null,
    conceptId: null,
    conceptResources: null,
    extraValidation: {}, // this is for validation of things that aren't handled by antD.
    formError: {show: false, type: null, message: null},
    preview: {enabled: false, title: null, fastExit: false},
}

function reducer(state, action) {
    if (action.type === ACTIONS.RESET) {
        return defaultDialogState;
    } else if (action.type === ACTIONS.GENERIC_VALUE_UPDATE) {
        const newState = { ...state };
        for (const key in action.payload.newValues) {
            if (Object.prototype.hasOwnProperty.call(state, key)) {
                const value = action.payload.newValues[key];
                if (typeof value === "function") {
                    newState[key] = value(state[key]);
                } else {
                    newState[key] = value;
                }
            } else {
                console.warn(`Warning: Attempted to update a non-existent state property: "${key}"`);
            }
        }
        return newState;
    } else if (action.type === ACTIONS.OPEN_PREVIEW) {
        const {title, fastExit} = action.payload;
        return {...state, preview: {enabled: true, title: title || null, fastExit: fastExit || false}};
    } else if (action.type === ACTIONS.CLOSE_PREVIEW) {
        return {...state, preview: {...state.preview, enabled: false}}
    } else if (action.type === ACTIONS.UPDATE_FORM_ERROR) {
        const {show, type, message} = action.payload;
        return {...state, formError: {show: !!show, type: type || null, message: message || null}};
    } else if (action.type === ACTIONS.UPDATE_FORM_EXTRA_VALIDATION) {
        return {...state, extraValidation: {...state.extraValidation, ...action.payload}};
    } else {
        return state;
    }
}

const EditStepDialog = props => {
    const {open, data, readOnly, onClose} = props;
    const {pathwayActivity, stepInfo} = data || {};
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [form] = Form.useForm();
    const previewRef = useRef();

    const [dialogState, dispatch] = useReducer(reducer, defaultDialogState);

    const reset = useCallback(() => {
        form.resetFields();
        dispatch({type: ACTIONS.RESET});
    }, []);
    const updateStateValues = useCallback(newValues => {
        dispatch({type: ACTIONS.GENERIC_VALUE_UPDATE, payload: {newValues: newValues}});
    });
    const showPreviewMode = useCallback((title = null, fastExit = false) => {
        dispatch({type: ACTIONS.OPEN_PREVIEW, payload: {title, fastExit}});
    }, []);
    const closePreviewMode = useCallback(() => {
        dispatch({type: ACTIONS.CLOSE_PREVIEW});
    }, []);
    const updateFormExtraValidation = useCallback((validationObj) => {
        dispatch({type: ACTIONS.UPDATE_FORM_EXTRA_VALIDATION, payload: validationObj});
    }, []);
    const updateFormError = useCallback((show, type = null, message = null) => {
        dispatch({type: ACTIONS.UPDATE_FORM_ERROR, payload: {show, type, message}});
    }, []);

    useEffect(() => {
        const loadDialog = async () => {
            try {
                const initialData = await getDialogSetupValues();
                const stateUpdates = prepareDialog();

                if (!pathwayActivity.noConcept) {
                    updateStateValues({
                        ...initialData,
                        ...stateUpdates,
                        init: (pathwayActivity.noOptions || !data?.conceptSpecific),
                        loading: !(pathwayActivity.noOptions || !data?.conceptSpecific)
                    });
                } else {
                    updateStateValues({
                        ...initialData,
                        ...stateUpdates,
                        init: true,
                        loading: false,
                        inputState: prev => ({
                            ...prev,
                            ...(stateUpdates.inputState || {}), // Keep Practice/Other data
                            ...(stepInfo?.stepActivities.length > 0 && {
                                [INPUTS.EXERCISES]: stepInfo.stepActivities
                                    .filter(a => stepInfo.resourceType === RESOURCE_TYPES.TUTORIAL)
                                    .map(a => a.tabId || a.tutorialId)
                            })
                        })
                    });
                }
            } catch (error) {
                console.error("Failed to initialize dialog:", error);
            }
        }

        if (open) {
            loadDialog();
        } else {
            reset();
        }
    }, [open]);

    useEffect(() => {
        if (!open || !dialogState.init || !data?.conceptSpecific) return;

        const loadConceptData = async () => {
            updateStateValues({
                loading: true
            });
            await fetchResources(dialogState.topicId, dialogState.conceptId);
            const stateUpdates = prepareDialog();
            updateStateValues({
                ...stateUpdates,
                inputState: prev => ({
                    ...prev,
                    ...stateUpdates.inputState
                }),
                loading: false
            });
        };

        loadConceptData();

    }, [dialogState.conceptId, dialogState.init]);

    useEffect(() => {
        if (!dialogState.preview.enabled && previewRef.current) {
            previewRef.current.innerHTML = "";
            document.querySelectorAll("script[data-preview-script]").forEach(s => s.remove());
        }
    }, [dialogState.preview.enabled]);

    const getDialogSetupValues = async () => {
        let newActiveFields = [];
        let newAjaxData = {};
        let newTopicId = stepInfo?.topicId ?? null;
        let newConceptId = stepInfo?.conceptId ?? null;
        const promises = [];

        if (pathwayActivity.questionType === QUESTION_TYPES.PRACTICE) {
            newActiveFields.push(DIALOG_FIELDS.QUESTION_OPTIONS);
            if (!data.conceptSpecific) {
                newAjaxData.defaultPracticeQuestionLevels = defaultPracticeQuestionLevels;
            }
        } else if (pathwayActivity.resourceType === RESOURCE_TYPES.TUTORIAL && !pathwayActivity.noOptions) {
            newActiveFields.push(DIALOG_FIELDS.SELECT_EXERCISES);
            promises.push(axios.get("/trail/getTrailTutorialType.action", {params: { pathwayActivityId: pathwayActivity.pathwayActivityId }}));
        } else if (pathwayActivity.resourceType === RESOURCE_TYPES.TEST) {
            newActiveFields.push(DIALOG_FIELDS.TEST_SEARCH);
        } else if (pathwayActivity.resourceType === RESOURCE_TYPES.CUSTOM_ACTIVITY) {
            newActiveFields.push(DIALOG_FIELDS.CUSTOM_ACTIVITY_OPTIONS);
        } else {
            if (!(pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA && pathwayActivity.mediaType === MEDIA_TYPES.SCORCHER)) {
                if (!pathwayActivity.noOptions && data.conceptSpecific) newActiveFields.push(DIALOG_FIELDS.SELECT_LESSON_OPTIONS);
            }
            if (pathwayActivity.level && pathwayActivity.resourceType !== RESOURCE_TYPES.QUIZ) newActiveFields.push(DIALOG_FIELDS.STEP_LEVEL);
            if (pathwayActivity.total) newActiveFields.push(DIALOG_FIELDS.STEP_TOTAL);
            if (pathwayActivity.useDescription) {
                newActiveFields.push(DIALOG_FIELDS.STEP_NAME, DIALOG_FIELDS.STEP_DESCRIPTION);
                if (pathwayActivity.resourceType === RESOURCE_TYPES.SURVEY && !data.hasData) newActiveFields.push(DIALOG_FIELDS.STEP_SURVEY);
            }
            if (pathwayActivity.useMessage) newActiveFields.push(DIALOG_FIELDS.STEP_MSG);
            if (pathwayActivity.resourceType === RESOURCE_TYPES.QUIZ) newActiveFields.push(DIALOG_FIELDS.STEP_QUIZ_LEVEL);
        }
        if (pathwayActivity.useSection && brandConfig.enableSectionsInPathways) {
            newActiveFields.push(DIALOG_FIELDS.STEP_SECTION);
            promises.push(axios.get("/trail/getTrailSections.action"));
        }
        if (stepInfo?.conceptId && user.conceptId == null) {
            promises.push(axios.get("/web/ui/content/syllabus/" + user.syllabusId + "/firstConcept"));
        }

        const responses = await Promise.all(promises);
        responses.forEach(response => {
            if (response.config.url.includes("getTrailTutorialType") && response.data.success) {
                newAjaxData[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES] = response.data.tabBeans;
            }
            if (response.config.url.includes("getTrailSections") && response.data.sections.length > 0) {
                newAjaxData[AJAX_DATA_TYPES.TRAIL_SECTIONS] = response.data.sections;
            }
            if (response.config.url.includes("firstConcept")) {
                newTopicId = response.data.topicId;
                newConceptId = response.data.conceptId;
            }
        });
        return {
            activeFields: newActiveFields,
            ajaxData: newAjaxData,
            topicId: newTopicId,
            conceptId: newConceptId
        };
    }

    const fetchResources = async (topicId, conceptId) => {
        try {
            const params = {
                ...(pathwayActivity.pathwayActivityId && { pathwayActivityId: pathwayActivity.pathwayActivityId }),
                ...(topicId && { topicId: topicId }),
                ...(conceptId && { conceptId: conceptId })
            }
            const response = await axios.post("/trail/findConceptResourcesByActivity.action", null, {params});
            if (response.data.success && response.data.pathwayActivityStep.hasActivities) {
                if (response.data.pathwayActivityStep?.stepActivities.length > 0) {
                    if (stepInfo?.conceptId === conceptId && stepInfo?.stepActivities.length > 0) {
                        updateStateValues({
                            inputState: prev => ({
                                ...prev,
                                [INPUTS.ACTIVITIES]: stepInfo?.stepActivities.map(activity => {
                                    if (stepInfo?.resourceType === RESOURCE_TYPES.QUESTION) return activity.questionId;
                                    else if (stepInfo?.resourceType === RESOURCE_TYPES.MEDIA) return activity.mediaId;
                                    else if (stepInfo?.resourceType === RESOURCE_TYPES.QUESTION_CLUSTER) return activity.questionClusterId;
                                }, {})
                            })
                        });
                    }
                    if (response.data.pathwayActivity.resourceType === RESOURCE_TYPES.TUTORIAL) {
                        if (response.data.pathwayActivityStep.stepActivities.length > 0) {
                            updateStateValues({
                                inputState: prev => ({
                                    ...prev,
                                    [INPUTS.ACTIVITIES]: response.data.pathwayActivityStep.stepActivities.map(stepActivity => stepActivity.activityId)
                                })
                            });
                        }
                        if (response.data.pathwayActivityStep.stepActivities.length === 1) {
                            updateStateValues({
                                activeFields: dialogState.activeFields.filter(field => field !== DIALOG_FIELDS.SELECT_EXERCISES)
                            });
                        }
                    }
                    if (response.data.pathwayActivity.resourceType === RESOURCE_TYPES.QUESTION && response.data.pathwayActivity.questionType === QUESTION_TYPES.PRACTICE) {
                        updateStateValues({
                            inputState: prev => ({
                                ...prev,
                                [INPUTS.ACTIVITY_LEVEL]: stepInfo ? stepInfo?.stepActivities[0].activityLevel : response.data.pathwayActivityStep.stepActivities[0].questionLevel,
                                [INPUTS.ACTIVITIES]: Object.fromEntries(
                                    response.data.pathwayActivityStep.stepActivities.map(stepActivity => [
                                        stepActivity.questionLevel,
                                        stepInfo?.stepActivities[0].activityLevel === stepActivity.questionLevel ? stepInfo?.stepActivities[0].completionTotal : stepActivity.activityCount
                                    ])
                                )
                            })
                        });
                    }
                    updateFormError(false);
                } else {
                    updateStateValues({
                        activeFields: dialogState.activeFields.filter(field => field !== DIALOG_FIELDS.SELECT_LESSON_OPTIONS),
                        ...(response.data.pathwayActivity.resourceType === RESOURCE_TYPES.QUIZ && {
                            inputState: prev => ({
                                ...prev,
                                [INPUTS.ACTIVITY_LEVEL]: stepInfo ? stepInfo?.stepActivities[0].activityLevel : brandConfig.questionLevelNames.findIndex((_, i) => i + 1 > 1) + 1
                            })
                        })
                    });
                }
                updateStateValues({
                    conceptResources: response.data,
                });
            } else {
                updateStateValues({
                    conceptResources: null,
                    inputState: {}
                });
                updateFormError(true, "warning", `There are no ${response.data.pathwayActivityStep.descriptionPlural} in this lesson.`);
            }
        } catch (error) {
            if (!(!dialogState.init && selectConceptCheck())) {
                updateStateValues({
                    loading: false,
                    ...(conceptId === null && {
                        conceptResources: null,
                        inputState: {}
                    }),
                });
                if (conceptId === null && !pathwayActivity.noConcept) {
                    updateFormError(true, "warning", `There are no ${pathwayActivity.namePlural} in this lesson.`);
                }
            }
        }
    }

    const prepareDialog = () => {
        const stateUpdates = {};
        if (stepInfo) {
            const stepActivity = stepInfo?.stepActivities[0];
            form.setFieldsValue({
                ...(stepInfo?.completionTotal && { completionTotal: stepActivity.completionTotal }),
                ...(stepInfo?.activityLevel && { activityLevel: stepInfo.activityLevel }),
                ...(stepInfo?.sectionId && { sectionId: stepInfo.sectionId }),
                ...(stepInfo?.name && { stepName: stepInfo.name }),
                ...(stepInfo?.description && { stepDescription: stepInfo.description }),
                ...(stepInfo?.message && { stepMessage: stepInfo.message }),
            });
            if (!data.conceptSpecific && pathwayActivity.questionType === QUESTION_TYPES.PRACTICE) {
                stateUpdates.inputState = {
                    [INPUTS.ACTIVITIES]: {},
                    [INPUTS.ACTIVITY_LEVEL]: stepActivity.activityLevel
                };
                defaultPracticeQuestionLevels.forEach((practiceLevel) => {
                    const total = stepActivity.activityLevel === practiceLevel.questionLevel
                        ? stepActivity.completionTotal
                        : practiceLevel.activityCount;
                    stateUpdates.inputState[INPUTS.ACTIVITIES][practiceLevel.questionLevel] = total;
                });
            }
            if (stepActivity.resourceType === RESOURCE_TYPES.TEST) {
                if (stepActivity?.testId) {
                    stateUpdates.testId = stepActivity.testId;
                }
                if (readOnly) {
                    handlePreview(stepActivity);
                    return;
                }
            }
            if (stepActivity.resourceType === RESOURCE_TYPES.QUIZ) {
                // form.setFieldValue(INPUTS.ACTIVITY_LEVEL, stepActivity.activityLevel);
            }
            if (stepActivity.resourceType === RESOURCE_TYPES.CUSTOM_ACTIVITY) {
                form.setFieldsValue({
                    customActivity: {
                        customActivityId: stepActivity.customActivityId,
                        title: stepActivity.customActivityTitle,
                        message: stepActivity.customActivityMessage,
                        linkUrl: stepActivity.customActivityLinkUrl
                    }
                });
            }
        }
        return stateUpdates;
    }

    const searchTests = async (pagingParams, handleTableData, handlePagingData, abortController) => {
        try {
            let params = {
                archived: false,
                exam: false,
                pageNumber: pagingParams.pageNumber,
                pageSize: pagingParams.pageSize,
                hasQuestions: true,
                testOwnerSearchType: "TEACHER"
            }
            const response = await axios.post("/web/ui/test/teacher/search", params, {
                headers: {"Content-Type": "application/json"},
                signal: abortController.signal
            });
            handleTableData(response.data?.currPageContents ?? []);
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: response.data.totalResults ?? 0,
            });
            updateStateValues({
                currentPage: pagingParams.pageNumber
            })
        } catch (error) {
            console.error(error);
        }
    }

    const handleLessonOptionChange = (newValues) => {
        if (readOnly) return;

        updateStateValues({
            inputState: prev => ({
                ...prev, [INPUTS.ACTIVITIES]: newValues
            })
        });
        const error = lessonOptionValidation(newValues);
        updateFormExtraValidation({[DIALOG_FIELDS.SELECT_LESSON_OPTIONS]: error});
    }

    const handleExerciseOptionChange = (newValues) => {
        if (readOnly) return;

        updateStateValues({
            inputState: prev => ({
                ...prev, [INPUTS.EXERCISES]: newValues
            })
        });
        const error = exercisesOptionValidation(newValues);
        updateFormExtraValidation({[DIALOG_FIELDS.SELECT_EXERCISES]: error});
    }

    const handleSyllabusChange = (concept, init) => {
        updateStateValues({
            ...(init && {init: true}),
            ...(concept?.topicId && {topicId: concept.topicId}),
            conceptId: concept?.conceptId ?? null,
            inputState: prev => ({...prev, [INPUTS.ACTIVITIES]: []}),
        });
    }

    const handlePreview = resource => {
        updateStateValues({
            loading: true
        });
        const loadActionURL = ({url, params, removeList, callback}) => {
            axios.get(url,{params: params})
                .then(response => {
                    previewRef.current.innerHTML = response.data;
                    if (removeList) previewRef.current.querySelectorAll(removeList).forEach(el => el.remove());
                    const scripts = previewRef.current.querySelectorAll("script");
                    scripts.forEach((script) => {
                        const newScript = document.createElement("script");
                        if (script.src) {
                            newScript.src = script.src;
                        } else {
                            newScript.textContent = script.textContent;
                        }
                        document.body.appendChild(newScript);
                        document.body.removeChild(newScript);
                    });

                    if (previewRef.current && window.MathJax) {
                        window.MathJax.typesetPromise([previewRef.current]);
                    }

                    if (callback && typeof callback === "function") callback(previewRef.current);

                    updateStateValues({
                        loading: false
                    });
                })
        }
        let title = "";

        if (dialogState.conceptResources == null) {
            if (pathwayActivity?.resourceType === RESOURCE_TYPES.TEST) {
                title = "Test" + ": " + (resource.name ? resource.name : resource.stepActivityName) + " (preview)";
                const url = "/controlCentre/showTestPreview.action";
                const params = {testId: resource.testId};
                loadActionURL({url: url, params: params, callback: (preview) => {
                    const inputs = preview.getElementsByTagName("input");
                    for (const el of inputs) {
                        el.setAttribute("disabled", true);
                    }
                }});
            }
        } else if (dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA) {
            if (dialogState.conceptResources.pathwayActivity.mediaType === MEDIA_TYPES.WORKSHEET) {
                openMediaDocument(resource.activityId);
            } else {
                if (dialogState.conceptResources.pathwayActivity.mediaType === MEDIA_TYPES.WIDGET) {
                    title = resource.activityName + " (preview)";
                    const url = "/lessonSection/displayWidgetEmbedded.action";
                    loadActionURL({url: url, params: {mediaId: resource.activityId, dialogWidget: true}});
                } else if (dialogState.conceptResources.pathwayActivity.mediaType === MEDIA_TYPES.VIDEO) {
                    title = resource.activityName + " (preview)";
                    const url = "/lessonSection/displayVideoEmbedded.action";
                    loadActionURL({url: url, params: {mediaId: resource.activityId, previewView: true}, removeList: ".gen-sub-appBar"});
                } else if (dialogState.conceptResources.pathwayActivity.mediaType === MEDIA_TYPES.DESMOS) {
                    title = resource.activityName + " (preview)";
                    const url = "/lessonSection/displayGraphingCalculatorEmbedded.action";
                    loadActionURL({url: url, params: {mediaId: resource.activityId, previewView: true}, removeList: ".gen-sub-appBar"});
                }
            }
        } else if (dialogState.conceptResources.pathwayActivity.questionType === QUESTION_TYPES.WALKTHROUGH) {
            title = QUESTION_TYPES.WALKTHROUGH + ": " + resource.activityName + " (preview)";
            const url = "/lessonSection/loadWalkthrough.action";
            const params = {questionId: resource.activityId, isSummary: false, conceptId: dialogState.conceptResources.conceptId};
            loadActionURL({url: url, params: params, removeList: ".js-startWalkthough"});
        } else if (dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.QUESTION_CLUSTER) {
            if (dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.TUTORIAL) {
                //     openTutorialContent(activityId, 'Section', height, width);
            } else if (dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.QUESTION_CLUSTER) {
                title = resource.activityName + " (preview)";
                const url = "/lessonSection/loadInlineQuestionsByCluster.action";
                const params = {questionClusterId: resource.activityId, conceptId: dialogState.conceptResources.conceptId, preview: true, readOnly: true, singleQuestionView: false};
                loadActionURL({url: url, params: params, removeList: ".js-questionClusterSwitches"});
            }
        }
        const fastExit = readOnly && stepInfo?.resourceType === RESOURCE_TYPES.TEST;
        if (title) {
            showPreviewMode(title, fastExit);
        } else {
            updateStateValues({
                loading: false
            });
        }
    }

    const exitPreview = () => {
        if (dialogState.preview.enabled && !dialogState.preview.fastExit) {
            closePreviewMode();
        } else {
            handleClose();
        }
    }

    const handleClose = () => {
        form.resetFields();
        if (onClose && typeof onClose === "function") onClose();
    }

    const validateForm = async () => {
        // This is a stupid validation check because antD is dumb and stubborn.
        let extraValidationErrors = {};
        if (dialogState.activeFields.includes(DIALOG_FIELDS.SELECT_LESSON_OPTIONS)) {
            const activities = dialogState.inputState?.[INPUTS.ACTIVITIES] || [];
            extraValidationErrors[DIALOG_FIELDS.SELECT_LESSON_OPTIONS] = lessonOptionValidation(activities);
        }
        if (dialogState.activeFields.includes(DIALOG_FIELDS.SELECT_EXERCISES)) {
            const exercises = dialogState.inputState?.[INPUTS.EXERCISES] || [];
            extraValidationErrors[DIALOG_FIELDS.SELECT_EXERCISES] = exercisesOptionValidation(exercises);
        }
        if (dialogState.activeFields.includes(DIALOG_FIELDS.TEST_SEARCH)) {
            extraValidationErrors[DIALOG_FIELDS.TEST_SEARCH] = testSearchValidation();
        }
        if (data.conceptSpecific && dialogState.activeFields.includes(DIALOG_FIELDS.QUESTION_OPTIONS)) {
            const totalQuestions = dialogState.inputState?.[INPUTS.ACTIVITIES][dialogState.inputState?.[INPUTS.ACTIVITY_LEVEL]];
            extraValidationErrors[DIALOG_FIELDS.QUESTION_OPTIONS] = questionOptionValidation(totalQuestions);
        }
        updateFormExtraValidation(extraValidationErrors);
        if (Object.values(extraValidationErrors).some(validation => validation !== null)) return false;

        const fieldsError = form.getFieldsError().some(field => field.errors.length > 0);
        if (fieldsError) return false;

        try {
            await form.validateFields();
            return true;
        } catch (err) {
            return false;
        }
    }
    const lessonOptionValidation = (activities) => {
        return activities.length > 0 ? null : {error: true}
    }
    const exercisesOptionValidation = (exercises) => {
        return exercises.length > 0 ? null : {error: true}
    }
    const testSearchValidation = () => {
        return dialogState?.testId ? null : {error: true}
    }
    const questionOptionValidation = (totalQuestions) => {
        return totalQuestions > 0 ? null : {error: true}
    }
    const selectConceptCheck = () => {
        return pathwayActivity?.noConcept != true && !(pathwayActivity?.useSection && brandConfig?.enableSectionsInPathways);
    }

    return (
        <GenericDialog
            title={!dialogState.preview.enabled ? data?.title : dialogState.preview.title}
            contentStyle={{
                minWidth: 560
            }}
            open={open}
            onClose={() => (dialogState.preview.enabled && !dialogState.preview.fastExit ? exitPreview() : handleClose())}
            buttons={(
                !dialogState.preview.enabled
                    ? !readOnly
                        ? [ {
                                name: 'Cancel',
                                extraProps: { type: 'text' },
                            },
                            {
                            name: data?.submitTitle,
                            disabled: dialogState.loading || dialogState.submitting || dialogState.formError.show || form.getFieldsError().some(field => field.errors.length > 0),
                            callback: async () => {
                                try {
                                    const isValid = await validateForm();
                                    if (!isValid) return;
                                    updateStateValues({
                                        submitting: true,
                                    });

                                    const url = !stepInfo ? "/trail/createStep.action" : "/trail/editStep.action";
                                    const params = {
                                        pathwayType: pathwayActivity.pathwayType,
                                        resourceType: pathwayActivity.resourceType,
                                        pathId: data.hasData ? stepInfo.pathId : pathwayActivity.pathId,
                                        stepId: data.hasData ? stepInfo.stepId : pathwayActivity.stepId,
                                        pathwayActivityId: pathwayActivity.pathwayActivityId,
                                        conceptId: dialogState.conceptId
                                    }

                                    const formValues = form.getFieldsValue();
                                    const formData = new URLSearchParams();
                                    Object.entries(form.getFieldsValue()).forEach(([key, value]) => {
                                        if (pathwayActivity.resourceType !== RESOURCE_TYPES.CUSTOM_ACTIVITY) {
                                            params[key] = value;
                                        }
                                    });

                                    if (!data.conceptSpecific) params.sectionId = formValues.sectionId;

                                    if (pathwayActivity?.resourceType === RESOURCE_TYPES.CUSTOM_ACTIVITY) {
                                        if (stepInfo) formData.append("customActivity.customActivityId", stepInfo?.stepActivities[0].customActivityId);
                                        Object.entries(formValues.customActivity).forEach(([key, value]) => {
                                            if (value) formData.append(`customActivity.${key}`, value);
                                        });
                                    } else {
                                        if (data.conceptSpecific &&
                                                !(data.pathwayActivity.resourceType === RESOURCE_TYPES.SURVEY) &&
                                                !(data.pathwayActivity.resourceType === RESOURCE_TYPES.TEST) &&
                                                !(pathwayActivity?.questionType === QUESTION_TYPES.PRACTICE) &&
                                                !(data.pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA && data.pathwayActivity.mediaType === MEDIA_TYPES.SCORCHER)) {
                                            dialogState.conceptResources?.pathwayActivityStep.stepActivities.map((activity, i) => {
                                                if (dialogState.inputState[INPUTS.ACTIVITIES] == null || (dialogState.inputState[INPUTS.ACTIVITIES]?.includes(activity.activityId))) {
                                                    const resourceType = pathwayActivity?.resourceType
                                                        ?.toLowerCase()
                                                        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
                                                    const activityName = INPUTS.ACTIVITIES + "[" + i + "]";
                                                    const activityLevel = activity.activityLevel ? activity.activityLevel : (activity.questionLevel ? activity.questionLevel : null);
                                                    const completionTotal = activity.completionTotal ? activity.completionTotal : 1;

                                                    formData.append(activityName + ".resourceType", pathwayActivity.resourceType);
                                                    if (pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA) formData.append(activityName + ".mediaType", pathwayActivity.mediaType);
                                                    if (activity.activityId) formData.append(activityName + "." + resourceType + "Id", activity.activityId);
                                                    if (activityLevel) formData.append(activityName + ".activityLevel", activityLevel);
                                                    formData.append(activityName + ".completionTotal", completionTotal);
                                                }
                                            });
                                        } else {
                                            const activityName = INPUTS.ACTIVITIES + "[0]";
                                            formData.append(activityName + ".resourceType", pathwayActivity.resourceType);
                                            if (pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA) formData.append(activityName + ".mediaType", pathwayActivity.mediaType);
                                            if (pathwayActivity.resourceType === RESOURCE_TYPES.TEST) formData.append(activityName + ".testId", dialogState.testId);
                                            if (dialogState.inputState[INPUTS.ACTIVITY_LEVEL]) formData.append(activityName + ".activityLevel", dialogState.inputState[INPUTS.ACTIVITY_LEVEL]);
                                            if (pathwayActivity?.questionType === QUESTION_TYPES.PRACTICE) {
                                                const completionTotal = dialogState.inputState[INPUTS.ACTIVITIES][dialogState.inputState[INPUTS.ACTIVITY_LEVEL]];
                                                formData.append(activityName + ".completionTotal", completionTotal);
                                            }
                                        }
                                    }
                                    axios.post(url, formData, {
                                        params: params,
                                        headers: {"Content-Type": "application/x-www-form-urlencoded"}
                                    })
                                        .then(response => {
                                            if (response.data.success) {
                                                if (response.data.paths != undefined) {
                                                    $(document).trigger("updatePathway", response.data);
                                                }
                                                // ??? Do I need this?
                                                // if (data.pathway.publishState && $('.js-publishState').val() != data.pathway.publishState) {
                                                //     $('.js-publishState').val(data.pathway.publishState).change();
                                                // }
                                                handleClose();
                                            }
                                        })
                                        .finally(() => {
                                            updateStateValues({
                                                submitting: false,
                                            });
                                        });
                                } catch (error) {
                                    console.log('ERROR: CREATE / EDIT STEP', error);
                                }
                            }
                        }]
                        : [{
                            name: 'Cancel',
                            extraProps: { type: 'text' },
                        }]
                    : [{
                        name: 'Cancel',
                        extraProps: { type: 'text' },
                    },{
                        name: "Exit preview",
                        callback: async () => {
                            exitPreview();
                        }
                    }]
            )}
        >
            {dialogState.loading && (
                <CircularLoader />
            )}
            <Form
                form={form}
                className="ch-form padding--m margin-b--rm"
                layout="vertical"
                disabled={readOnly}
                style={{
                    display: (dialogState.preview.enabled ? "none" : "block"),
                    visibility: (dialogState.loading ? "hidden" : "visible")
                }}
                onValuesChange={() => {
                    setTimeout(() => {
                        const hasErrors = form.getFieldsError().some(f => f.errors.length > 0);
                        if (hasErrors) {
                            // console.log("There are form errors");
                        }
                    }, 0);
                }}
            >
                {selectConceptCheck() && (
                    <GenericSyllabusTopicConceptSelect
                        form={form}
                        selectLevel={pathwayActivity?.mediaLocation?.toLowerCase() ?? "concept"}
                        disabled={readOnly}
                        onChange={concept => handleSyllabusChange(concept)}
                        onLoadComplete={concept => handleSyllabusChange(concept, true)}
                        initialIds={stepInfo ?? null}
                        resourceType={pathwayActivity?.resourceType}
                        questionClusterTypes={pathwayActivity?.questionClusterTypes}
                        autoMark={pathwayActivity?.autoMark}
                        questionType={pathwayActivity?.questionType}
                        mediaType={pathwayActivity?.mediaType}
                        includeSupplementary={true}
                    />
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_SECTION) && !dialogState.loading && (
                    // js-newStep-section
                    <Form.Item
                        name="sectionId"
                        label="Chapter part"
                        initialValue={stepInfo?.sectionId ?? ""}
                    >
                        <Select
                            options={[
                                { value: "", label: "Any" },
                                ...(dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_SECTIONS] || []).map(section => ({
                                    value: section.sectionId,
                                    label: section.name,
                                })),
                            ]}
                        />
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_LEVEL) && !dialogState.loading && (
                    <div class="gen-input js-newStep-level">
                        <div class="gen-input-label gen-input-req">Step difficulty</div>
                        <div class="gen-select">
                            <select name="activityLevel">
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                            </select>
                        </div>
                    </div>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_QUIZ_LEVEL) && !dialogState.loading && (
                    // js-newQuiz-level
                    <Form.Item
                        label="Step difficulty"
                    >
                        <Select
                            value={dialogState.inputState[INPUTS.ACTIVITY_LEVEL] ?? undefined}
                            onChange={(val) => {
                                updateStateValues({
                                    inputState: prev => ({
                                        ...prev,
                                        [INPUTS.ACTIVITY_LEVEL]: val,
                                    })
                                });
                            }}
                            options={brandConfig.questionLevelNames
                                .map((questionLevel, i) => ({value: i + 1, label: questionLevel}))
                                .filter(option => option.value > 1)}
                        />
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_TOTAL) && !dialogState.loading && (
                    <div class="gen-input gen-input-req js-newStep-total">
                        <div class="gen-input-label">Total*</div>
                        {/*<input type="text" class="gen-textfield" name="completionTotal" gen-input-type="integer">*/}
                    </div>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_NAME) && !dialogState.loading && (
                    <>
                        {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_SURVEY) && (
                            <p><b>You can name this self-assessment point and give students instructions on what
                                components they are self-assessing in the description field.</b></p>
                        )}
                        <Form.Item
                            label="Name"
                            name="stepName"
                        >
                            <Input placeholder="Progress rating"/>
                        </Form.Item>
                    </>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_DESCRIPTION) && !dialogState.loading && (
                    <Form.Item
                        label="Description"
                        name="stepDescription"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder='Ask your students to complete a task that is not otherwise listed in the available set of activities, e.g. "Read through the Key Ideas from section 1C and complete the Building Understanding questions." You can direct students to a specific page by including a URL.'
                        />
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.STEP_MSG) && !dialogState.loading && (
                    <Form.Item
                        label="Message for students"
                        name="stepMessage"
                    >
                        <Input.TextArea
                            rows={4}
                            {...(!readOnly && {placeholder: pathwayActivity?.messagePlaceholder})}
                        />
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.SELECT_LESSON_OPTIONS) && !dialogState.loading && (!selectConceptCheck() || dialogState.conceptId) && (
                    // js-modal-selectLessonOptions
                    <Form.Item
                        label="Options"
                        required={true}
                        extra={dialogState.extraValidation?.[DIALOG_FIELDS.SELECT_LESSON_OPTIONS] && (
                            <div className="ant-form-item-explain-error">Please select at least one option.</div>
                        )}
                        validateStatus={dialogState.extraValidation?.[DIALOG_FIELDS.SELECT_LESSON_OPTIONS] ? "error" : ""}
                    >
                        {dialogState.conceptResources?.pathwayActivityStep?.stepActivities.length > 1 && (
                            <Checkbox
                                className="checkAll"
                                indeterminate={
                                    dialogState.inputState[INPUTS.ACTIVITIES]?.length > 0 &&
                                    dialogState.inputState[INPUTS.ACTIVITIES]?.length !== dialogState.conceptResources.pathwayActivityStep.stepActivities.length
                                }
                                checked={
                                    dialogState.inputState[INPUTS.ACTIVITIES]?.length > 0 &&
                                    dialogState.inputState[INPUTS.ACTIVITIES]?.length === dialogState.conceptResources.pathwayActivityStep.stepActivities.length
                                }
                                onChange={(e) => {
                                    handleLessonOptionChange(e.target.checked
                                        ? dialogState.conceptResources?.pathwayActivityStep.stepActivities
                                            .filter(activity => activity.activityId)
                                            .map(activity => activity.activityId)
                                        : []);
                                }}
                            >
                                All
                            </Checkbox>
                        )}
                        <Checkbox.Group
                            className="flexColumn"
                            value={dialogState.inputState[INPUTS.ACTIVITIES] || []}
                            onChange={(checkedValues) => handleLessonOptionChange(checkedValues)}
                        >
                            {dialogState.conceptResources?.pathwayActivityStep?.stepActivities.map((resource, i) => {
                                const labelText = resource.activityName +
                                    (dialogState.conceptResources.pathwayActivity?.resourceType === RESOURCE_TYPES.QUESTION_CLUSTER &&
                                    dialogState.conceptResources.pathwayActivity?.questionClusterType !== "QUICK_QUIZ"
                                        ? (resource.autoMark ? " (auto-marked)" : "") : "");
                                const checkbox = (
                                    <div key={resource.activityId}
                                         className={"flexRow flexAlignCentre" + (i > 0 ? " margin-t--m" : "")}>
                                        <Checkbox
                                            className="flex js-activityResource"
                                            value={resource.activityId}
                                        >
                                            {labelText}
                                        </Checkbox>
                                        {(
                                            dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.MEDIA ||
                                            dialogState.conceptResources.pathwayActivity.questionType === QUESTION_TYPES.WALKTHROUGH ||
                                            dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.QUESTION_CLUSTER ||
                                            dialogState.conceptResources.pathwayActivity.resourceType === RESOURCE_TYPES.TUTORIAL
                                        ) && (
                                            <Button
                                                variant="outlined"
                                                disabled={false}
                                                onClick={() => handlePreview(resource)}
                                            >
                                                Preview
                                            </Button>
                                        )}
                                    </div>
                                );
                                return checkbox;
                                // return resource.activityImg ? (
                                //     <div className="activityImgBlk" key={resource.activityId}>
                                //         {checkbox}
                                //         <img
                                //             alt={resource.activityName}
                                //             src={resource.activityImg}
                                //             className={"linkClass" +
                                //                 {
                                //                     [RESOURCE_TYPES.MEDIA]: " js-media-link",
                                //                     [RESOURCE_TYPES.WALKTHROUGH]: " js-walkthrough-link",
                                //                     [RESOURCE_TYPES.QUESTION_CLUSTER]: " js-questionCluster-link",
                                //                     [RESOURCE_TYPES.TUTORIAL]: " js-tutorial-link"
                                //                 }[dialogState.conceptResources.pathwayActivity.resourceType] ?? ""
                                //             }
                                //             data-id={resource.activityId}
                                //             data-type={dialogState.conceptResources.pathwayActivity?.mediaType?.toLowerCase() ?? ''}  // mediaType
                                //         />
                                //     </div>
                                // ) : (checkbox);
                            })}
                        </Checkbox.Group>
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.SELECT_EXERCISES) && !dialogState.loading && (
                    // js-modal-selectExercises
                    <Form.Item
                        label="Section"
                        required={true}
                        extra={dialogState.extraValidation?.[DIALOG_FIELDS.SELECT_EXERCISES] && (
                            <div className="ant-form-item-explain-error">Please select at least one option.</div>
                        )}
                        validateStatus={dialogState.extraValidation?.[DIALOG_FIELDS.SELECT_EXERCISES] ? "error" : ""}
                    >
                        {dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES]?.length > 1 && (
                            <Checkbox
                                className="checkAll"
                                indeterminate={
                                    dialogState.inputState[INPUTS.EXERCISES]?.length > 0 &&
                                    dialogState.inputState[INPUTS.EXERCISES]?.length !== dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES]?.length
                                }
                                checked={
                                    dialogState.inputState[INPUTS.EXERCISES]?.length > 0 &&
                                    dialogState.inputState[INPUTS.EXERCISES]?.length === dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES]?.length
                                }
                                onChange={(e) => {
                                    handleExerciseOptionChange(e.target.checked
                                        ? dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES]
                                            .filter(tab => tab.tabId)
                                            .map(tab => tab.tabId)
                                        : []);
                                }}
                            >
                                All
                            </Checkbox>
                        )}
                        <Checkbox.Group
                            className="flexColumn"
                            value={dialogState.inputState[INPUTS.EXERCISES] || []}
                            onChange={(checkedValues) => handleExerciseOptionChange(checkedValues)}
                        >
                            {(dialogState.ajaxData?.[AJAX_DATA_TYPES.TRAIL_TUTORIAL_TYPES] || []).map(tab => {
                                return (
                                    <Checkbox
                                        key={tab.tabId}
                                        className="flex js-activityResource"
                                        value={tab.tabId}
                                    >
                                        {tab.tabName}
                                    </Checkbox>
                                )
                            })}
                        </Checkbox.Group>
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.TEST_INFO) && !dialogState.loading && (
                    // js-modal-test-info
                    <>
                        TEST INFO!!!
                    </>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.TEST_SEARCH) && !readOnly && !dialogState.loading && (
                    // js-modal-test-search
                    <Form.Item
                        extra={dialogState.extraValidation?.[DIALOG_FIELDS.TEST_SEARCH] && (
                            <div className="ant-form-item-explain-error">Please select a test</div>
                        )}
                        validateStatus={dialogState.extraValidation?.[DIALOG_FIELDS.TEST_SEARCH] ? "error" : ""}
                    >
                        <TableAntD
                            columnData={[
                                {
                                    dataIndex: "selected",
                                    render: (_, record) => (
                                        <Radio
                                            checked={record.testId === dialogState.testId}
                                            onChange={() => updateStateValues({testId: record.testId})}
                                        />
                                    ),
                                }, {
                                    title: "Test name",
                                    dataIndex: "name",
                                }, {
                                    title: "Questions",
                                    dataIndex: "questionCount",
                                }, {
                                    title: "Owner",
                                    dataIndex: "ownedBy",
                                }, {
                                    title: "Created",
                                    dataIndex: "createDate",
                                    render: (_, record) => {
                                        return user.dateFormat.format(record.createDate)
                                    },
                                }, {
                                    title: "Description",
                                    dataIndex: "description",
                                }, {
                                    key: "action",
                                    render: (_, record) => (
                                        <Button type="link" onClick={() => handlePreview(record)}>
                                            Preview
                                        </Button>
                                    ),
                                }
                            ]}
                            pagingData={{fetchFunction: searchTests, params: {pageSize: 10, currentPage: dialogState.currentPage}}}
                            rowKey="testId"
                            locale={{
                                emptyText: (
                                    <span
                                        className="ch-warning-text">No {brandConfig.testsName.toLowerCase()} found</span>
                                )
                            }}
                        />
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.QUESTION_OPTIONS) && !dialogState.loading && (
                    // js-modal-questionOptions
                    <Form.Item
                        extra={dialogState.extraValidation[DIALOG_FIELDS.QUESTION_OPTIONS] && (
                            <div className="ant-form-item-explain-error">Question count needs to be greater than
                                0.</div>
                        )}
                    >
                        <div className="flexColumn ch-formGap">
                            {(dialogState.conceptResources?.pathwayActivityStep?.stepActivities ||
                                dialogState.ajaxData.defaultPracticeQuestionLevels || []).map((questionActivity, i) => {

                                const level = questionActivity.questionLevel || (i + 1);
                                const maxCount = questionActivity.activityCount || 15;
                                const levelName = brandConfig.questionLevelNames?.[i] || `Level ${level}`;

                                return (
                                    <Form.Item
                                        key={level}
                                        label={`How many ${levelName} questions`}
                                        validateStatus={dialogState.extraValidation[DIALOG_FIELDS.QUESTION_OPTIONS] ? "error" : ""}
                                    >
                                        <div className="flex">
                                            <Radio
                                                checked={dialogState.inputState[INPUTS.ACTIVITY_LEVEL] === level}
                                                onChange={() => {
                                                    updateStateValues({
                                                        inputState: prev => ({
                                                            ...prev,
                                                            [INPUTS.ACTIVITY_LEVEL]: level
                                                        })
                                                    });
                                                    const currentVal = dialogState.inputState[INPUTS.ACTIVITIES]?.[level] || 0;
                                                    const error = questionOptionValidation(currentVal);
                                                    updateFormExtraValidation({[DIALOG_FIELDS.QUESTION_OPTIONS]: error});
                                                }}
                                            />
                                            <Select
                                                name={"activities[" + level + "]"}
                                                value={(() => {
                                                    if (dialogState.inputState[INPUTS.ACTIVITY_LEVEL] !== level) return 0;
                                                    const val = dialogState.inputState[INPUTS.ACTIVITIES]?.[level];
                                                    return val === maxCount ? "all" : (val ?? 0);
                                                })()}
                                                disabled={readOnly || (dialogState.inputState[INPUTS.ACTIVITY_LEVEL] !== level)}
                                                virtual={false}
                                                showSearch={false}
                                                onChange={value => {
                                                    const actualValue = value === "all" ? maxCount : value;
                                                    updateStateValues({
                                                        inputState: prev => ({
                                                            ...prev,
                                                            [INPUTS.ACTIVITIES]: {
                                                                ...prev[INPUTS.ACTIVITIES],
                                                                [level]: actualValue
                                                            }
                                                        })
                                                    });
                                                    const error = questionOptionValidation(actualValue);
                                                    updateFormExtraValidation({[DIALOG_FIELDS.QUESTION_OPTIONS]: error});
                                                }}
                                                options={[
                                                    {value: "all", label: "All"},
                                                    {value: 0, label: "None"},
                                                    ...Array.from({length: maxCount}, (_, i) => ({
                                                        value: i + 1,
                                                        label: (i + 1).toString(),
                                                    })),
                                                ]}
                                            />
                                        </div>
                                    </Form.Item>
                                );
                            })}
                        </div>
                    </Form.Item>
                )}
                {dialogState.activeFields.includes(DIALOG_FIELDS.CUSTOM_ACTIVITY_OPTIONS) && !dialogState.loading && (
                    // js-customActivity
                    <>
                        <Form.Item
                            label="Title"
                            name={["customActivity", "title"]}
                            rules={[{required: true, message: "Title is required"}]}
                        >
                            <Input
                                placeholder={"Custom activity title"}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Message"
                            name={["customActivity", "message"]}
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder='Ask your students to complete a task that is not otherwise listed in the available set of activities, e.g. "Read through the Key Ideas from section 1C and complete the Building Understanding questions." You can direct students to a specific page by including a URL.'
                            />
                        </Form.Item>
                        <Form.Item
                            label="External link"
                            name={["customActivity", "linkUrl"]}
                            validateTrigger="onChange"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        return validateUrl(value)
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("External URL is not valid"));
                                    },
                                },
                            ]}
                        >
                            <Input placeholder={"External link url"}/>
                        </Form.Item>
                    </>
                )}
                {dialogState.formError.show && (
                    <Alert className="margin-t--l" type={dialogState.formError?.type} message={dialogState.formError?.message}/>
                )}
            </Form>
            {dialogState.preview.enabled && (
                <div ref={previewRef} style={{display: (dialogState.preview.enabled && !dialogState.loading ? "unset" : "none")}}></div>
            )}
        </GenericDialog>
    )
}

export default EditStepDialog;